import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MobileLayout from '../MobileLayout';
import { apiGet } from '../api';
import { MobileDocument } from '../types';

interface ProjectFull {
  id: string;
  title: string;
  customerId: string;
  customer?: { id: string; name: string; company?: string | null };
  analytics?: { documentCount: number; reportCount: number };
}

const docKindIcon = (mime: string) => {
  if (mime === 'application/pdf') return '📄';
  if (mime.startsWith('image/')) return '🖼️';
  if (mime.includes('wordprocessing') || mime === 'application/msword') return '📝';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return '📊';
  if (mime.includes('rfc822') || mime.includes('outlook')) return '✉️';
  return '📎';
};

const MobileProject: React.FC = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<ProjectFull | null>(null);
  const [documents, setDocuments] = useState<MobileDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const p = await apiGet<ProjectFull>(`/projects/${projectId}`);
        if (cancel) return;
        setProject(p);
        const docs = await apiGet<any[]>(`/customers/${p.customerId}/documents`);
        if (cancel) return;
        const scoped = (docs || [])
          .filter((d) => d.projectId === projectId)
          .map((d): MobileDocument => ({
            id: d.id,
            name: d.name,
            type: d.type,
            size: d.size,
            status: d.status,
            projectId: d.projectId,
            customerId: d.customerId,
            uploadedAt: d.uploadedAt,
            fileUrl: d.fileUrl,
          }));
        setDocuments(scoped);
      } catch (e) {
        if (!cancel) setError(e instanceof Error ? e.message : 'Failed to load project');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [projectId]);

  return (
    <MobileLayout title={project?.title || 'Project'} back>
      {loading && <div className="px-3 py-6 text-center text-sm text-slate-500">Loading…</div>}
      {error && <div className="mx-3 my-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">{error}</div>}

      {project && (
        <>
          <section className="mx-3 mt-3 grid grid-cols-2 gap-3">
            <Link
              to={`/m/projects/${project.id}/capture`}
              className="flex flex-col items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-4 text-white shadow-sm active:opacity-90"
              style={{ backgroundColor: '#1993e5' }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7"><path d="M9 2 7.2 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3.2L15 2H9zm3 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" /></svg>
              <span className="text-sm font-semibold">Add Photos</span>
            </Link>
            <Link
              to={`/m/projects/${project.id}/run`}
              className="flex flex-col items-center justify-center gap-1.5 rounded-xl bg-white px-3 py-4 text-slate-900 shadow-sm ring-1 ring-slate-200 active:bg-slate-50 dark:bg-slate-900 dark:text-white dark:ring-slate-700 dark:active:bg-slate-800"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7" style={{ color: '#1993e5' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z" /></svg>
              <span className="text-sm font-semibold">Run Report</span>
            </Link>
          </section>

          <section className="mt-4 px-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Documents ({documents.length})</h2>
          </section>

          <ul className="mt-2 divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
            {documents.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-3 py-3">
                <span className="text-xl" aria-hidden>{docKindIcon(d.type)}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-900 dark:text-white">{d.name}</div>
                  <div className="text-[11px] text-slate-400">
                    {(d.size / 1024).toFixed(0)} KB · {d.status || 'ready'}
                  </div>
                </div>
              </li>
            ))}
            {documents.length === 0 && (
              <li className="px-3 py-8 text-center text-sm text-slate-500">
                No documents yet. Tap "Add Photos" to capture some.
              </li>
            )}
          </ul>
        </>
      )}
    </MobileLayout>
  );
};

export default MobileProject;
